// ==============================================
// Discovery — the map is the query.
//
// A skipper does not know what a stranger called their passage. They know where
// they are going. So the two questions this answers are "what is here?" and
// "who has been through this gap?", and neither is a text box.
//
// See docs/core/architecture/NAV_SPATIAL_DISCOVERY.md.
// ==============================================

import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useMap, useMapEvents, Polyline, CircleMarker, Tooltip } from 'react-leaflet';
import type L from 'leaflet';
import { api } from '../../services/api';
import type { NavElement, PlaceHit } from '../../types/nav';

export type LatLon = [number, number];
export interface Viewport { west: number; south: number; east: number; north: number }

const boundsToViewport = (b: L.LatLngBounds): Viewport => ({
  west: b.getWest(), south: b.getSouth(), east: b.getEast(), north: b.getNorth(),
});

const bboxParam = (v: Viewport) => `${v.west},${v.south},${v.east},${v.north}`;

/** Roughly how far the view must move before re-searching is worth offering. */
function movedEnough(a: Viewport | null, b: Viewport): boolean {
  if (!a) return true;
  const span = Math.max(0.0001, Math.abs(a.north - a.south));
  return (
    Math.abs(a.north - b.north) > span * 0.15
    || Math.abs(a.south - b.south) > span * 0.15
    || Math.abs(a.west - b.west) > span * 0.15
    || Math.abs(a.east - b.east) > span * 0.15
  );
}

// ---------------------------------------------------------------- map wiring

/**
 * Reports the viewport as the map moves, and never searches on its own.
 *
 * Results that reshuffle under the cursor while panning make a map impossible
 * to read, so movement only offers a re-search — the skipper decides when to
 * take it.
 */
export function ViewportWatcher({ onMove }: { onMove: (v: Viewport) => void }) {
  const map = useMapEvents({
    moveend: () => onMove(boundsToViewport(map.getBounds())),
    zoomend: () => onMove(boundsToViewport(map.getBounds())),
  });
  useEffect(() => { onMove(boundsToViewport(map.getBounds())); }, [map]);
  return null;
}

/** Fly the map to a place chosen from the gazetteer. */
export function FlyTo({ target }: { target: { lat: number; lon: number; spanDeg: number } | null }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    const half = Math.max(0.05, target.spanDeg / 2);
    map.flyToBounds(
      [[target.lat - half, target.lon - half], [target.lat + half, target.lon + half]],
      { maxZoom: 13, duration: 0.8 },
    );
  }, [map, target?.lat, target?.lon, target?.spanDeg]);
  return null;
}

/**
 * Draw a line across a pass. Click once for the first end, once for the second.
 * Escape, or a right-click, abandons it.
 */
export function GateDrawer({
  active, gate, onGate, onCancel,
}: {
  active: boolean;
  gate: [LatLon, LatLon] | null;
  onGate: (g: [LatLon, LatLon]) => void;
  onCancel: () => void;
}) {
  const [first, setFirst] = useState<LatLon | null>(null);
  const [hover, setHover] = useState<LatLon | null>(null);

  useMapEvents({
    click: (e) => {
      if (!active) return;
      const pt: LatLon = [e.latlng.lat, e.latlng.lng];
      if (!first) { setFirst(pt); return; }
      onGate([first, pt]);
      setFirst(null);
      setHover(null);
    },
    mousemove: (e) => { if (active && first) setHover([e.latlng.lat, e.latlng.lng]); },
    contextmenu: () => { if (active) { setFirst(null); setHover(null); onCancel(); } },
  });

  useEffect(() => {
    if (!active) { setFirst(null); setHover(null); return; }
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') { setFirst(null); setHover(null); onCancel(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [active, onCancel]);

  const rubber = first && hover ? [first, hover] : null;
  return (
    <>
      {gate && (
        <Polyline positions={gate} pathOptions={{ color: '#f0c25a', weight: 3, dashArray: '6 6' }}>
          <Tooltip sticky>Passages crossing this line</Tooltip>
        </Polyline>
      )}
      {rubber && <Polyline positions={rubber} pathOptions={{ color: '#f0c25a', weight: 2, opacity: 0.7, dashArray: '4 6' }} />}
      {first && <CircleMarker center={first} radius={5} pathOptions={{ color: '#f0c25a', fillOpacity: 1 }} />}
    </>
  );
}

/** Where each result crossed the line — the answer, marked on the map. */
export function CrossingMarkers({ points }: { points: LatLon[] }) {
  return (
    <>
      {points.map((p, i) => (
        <CircleMarker key={`${p[0]},${p[1]},${i}`} center={p} radius={6}
          pathOptions={{ color: '#f0c25a', weight: 2, fillColor: '#f0c25a', fillOpacity: 0.8 }} />
      ))}
    </>
  );
}

// ---------------------------------------------------------------- place search

export function PlaceSearch({ onPick }: { onPick: (p: PlaceHit) => void }) {
  const [q, setQ] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(q), 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, []);

  const { data: hits } = useQuery({
    queryKey: ['nav-places', debounced],
    queryFn: () => api.navPlaces(debounced),
    enabled: debounced.trim().length >= 2,
  });

  return (
    <div ref={boxRef} className="relative">
      <input
        type="search" value={q}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        placeholder="Where are you going?"
        className="w-56 rounded-lg border border-slate-600 bg-slate-800 px-3 py-1.5 text-sm text-white placeholder:text-slate-500"
      />
      {open && !!hits?.length && (
        <ul className="absolute z-[1200] mt-1 max-h-64 w-72 overflow-y-auto rounded-lg border border-slate-600 bg-slate-900 py-1 shadow-2xl">
          {hits.map((h) => (
            <li key={h.id}>
              <button
                type="button"
                onClick={() => { onPick(h); setOpen(false); setQ(h.name); }}
                className="block w-full px-3 py-1.5 text-left hover:bg-slate-800"
              >
                <span className="text-sm text-white">{h.name}</span>
                <span className="ml-1.5 text-[10px] uppercase tracking-wide text-slate-500">{h.kind}</span>
                {!!h.breadcrumb.length && (
                  <span className="block text-[11px] text-slate-400">{h.breadcrumb.join(' › ')}</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- the panel

export interface DiscoveryState {
  results: NavElement[];
  total: number;
  truncated: boolean;
  crossingAt: LatLon[];
  mode: 'area' | 'crossing';
}

export const EMPTY_DISCOVERY: DiscoveryState = {
  results: [], total: 0, truncated: false, crossingAt: [], mode: 'area',
};

export function useDiscovery() {
  const [state, setState] = useState<DiscoveryState>(EMPTY_DISCOVERY);

  const searchArea = useMutation({
    mutationFn: (args: { viewport: Viewport; q?: string; types?: string[] }) =>
      api.navDiscover({ bbox: bboxParam(args.viewport), q: args.q, types: args.types?.join(',') }),
    onSuccess: (r) => setState({
      results: r.items, total: r.total, truncated: r.truncated, crossingAt: [], mode: 'area',
    }),
  });

  const searchCrossing = useMutation({
    mutationFn: (gate: [LatLon, LatLon]) =>
      api.navCrossing(`${gate[0][0]},${gate[0][1]},${gate[1][0]},${gate[1][1]}`),
    onSuccess: (r) => setState({
      results: r.items.map((i) => i.element),
      total: r.total,
      truncated: r.total > r.items.length,
      crossingAt: r.items.map((i) => i.at),
      mode: 'crossing',
    }),
  });

  return { state, setState, searchArea, searchCrossing };
}

export { movedEnough, boundsToViewport };
